import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ShieldCheck, ShieldOff, Loader2, KeyRound, Copy, Check } from "lucide-react";
import {
  useTwoFactorStatus,
  useSetupTwoFactor,
  useConfirmTwoFactor,
  useDisableTwoFactor,
  type TwoFactorSetup,
} from "@/api/auth";
import { getApiErrorMessage } from "@/api/client";

/**
 * Configuración de seguridad de la cuenta — autenticación en dos pasos
 * (2FA/TOTP). Trabajo de continuación posterior a las 26 fases originales
 * del plan (ver docs/PROGRESO.md, cierre de la Fase 26).
 */
export default function SecuritySettingsPage() {
  const { data: status, isLoading } = useTwoFactorStatus();
  // Códigos de recuperación recién generados al confirmar la activación.
  // Vive en el padre (no en TwoFactorSetupFlow) a propósito: confirmar la
  // activación invalida y refresca `useTwoFactorStatus` en segundo plano
  // (para que el badge "Activada" se actualice), y ese refetch pondría
  // `status.enabled=true` justo cuando el usuario todavía está viendo los
  // códigos — si esta bandera viviera dentro de TwoFactorSetupFlow, ese
  // cambio de `status.enabled` desmontaría el componente (la rama de abajo
  // pasaría a TwoFactorEnabledPanel) y los códigos desaparecerían de la
  // pantalla antes de que el usuario alcanzara a guardarlos. Detectado en
  // vivo con Playwright antes de dar por terminada esta pantalla.
  const [justConfirmedCodes, setJustConfirmedCodes] = useState<string[] | null>(null);

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-xl font-semibold text-gray-900">Seguridad</h1>

      <div className="card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Autenticación en dos pasos (2FA)</h2>
            <p className="mt-1 text-sm text-gray-500">
              Añade una capa adicional de seguridad: además de su contraseña, deberá ingresar un código generado por
              una aplicación autenticadora (Google Authenticator, Authy, 1Password, etc.) cada vez que inicie sesión.
            </p>
          </div>
          {!isLoading && status && !justConfirmedCodes && (
            <span
              className={
                "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium " +
                (status.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600")
              }
            >
              {status.enabled ? <ShieldCheck size={13} /> : <ShieldOff size={13} />}
              {status.enabled ? "Activada" : "Desactivada"}
            </span>
          )}
        </div>

        {isLoading && <p className="mt-4 text-sm text-gray-400">Cargando...</p>}

        {!isLoading && status && (
          <div className="mt-5">
            {justConfirmedCodes ? (
              <RecoveryCodesPanel codes={justConfirmedCodes} onDone={() => setJustConfirmedCodes(null)} />
            ) : status.enabled ? (
              <TwoFactorEnabledPanel recoveryCodesRemaining={status.recoveryCodesRemaining} />
            ) : (
              <TwoFactorSetupFlow onConfirmed={setJustConfirmedCodes} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TwoFactorEnabledPanel({ recoveryCodesRemaining }: { recoveryCodesRemaining: number }) {
  const [showDisableForm, setShowDisableForm] = useState(false);
  const disableTwoFactor = useDisableTwoFactor();
  const { register, handleSubmit, reset } = useForm<{ currentPassword: string }>();

  const onDisable = async (values: { currentPassword: string }) => {
    try {
      await disableTwoFactor.mutateAsync(values.currentPassword);
      toast.success("Autenticación en dos pasos desactivada.");
      setShowDisableForm(false);
      reset();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Le quedan <span className="font-semibold">{recoveryCodesRemaining}</span> código(s) de recuperación sin usar.
      </p>

      {!showDisableForm ? (
        <button type="button" className="btn-secondary text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDisableForm(true)}>
          Desactivar autenticación en dos pasos
        </button>
      ) : (
        <form className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4" onSubmit={handleSubmit(onDisable)} noValidate>
          <p className="text-sm text-red-800">Confirme su contraseña actual para desactivar la autenticación en dos pasos.</p>
          <input
            type="password"
            placeholder="Contraseña actual"
            className="input-field"
            autoComplete="current-password"
            {...register("currentPassword", { required: true })}
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500" disabled={disableTwoFactor.isPending}>
              {disableTwoFactor.isPending ? "Desactivando..." : "Confirmar desactivación"}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowDisableForm(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

type SetupStep = "idle" | "confirm";

function TwoFactorSetupFlow({ onConfirmed }: { onConfirmed: (codes: string[]) => void }) {
  const [step, setStep] = useState<SetupStep>("idle");
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);

  const setupTwoFactor = useSetupTwoFactor();
  const confirmTwoFactor = useConfirmTwoFactor();
  const { register, handleSubmit, reset } = useForm<{ code: string }>();

  const startSetup = async () => {
    try {
      const result = await setupTwoFactor.mutateAsync();
      setSetup(result);
      setStep("confirm");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const onConfirm = async (values: { code: string }) => {
    try {
      const result = await confirmTwoFactor.mutateAsync(values.code.trim());
      reset();
      onConfirmed(result.recoveryCodes);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  if (step === "idle") {
    return (
      <button type="button" className="btn-primary" onClick={startSetup} disabled={setupTwoFactor.isPending}>
        {setupTwoFactor.isPending ? "Generando..." : "Activar autenticación en dos pasos"}
      </button>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-start">
        <img src={setup!.qrCodeDataUrl} alt="Código QR para configurar la autenticación en dos pasos" className="h-40 w-40 shrink-0 rounded bg-white p-1" />
        <div className="min-w-0 text-sm text-gray-600">
          <p>1. Escanee este código QR con su aplicación autenticadora.</p>
          <p className="mt-1">2. Si no puede escanear el QR, ingrese este código manualmente:</p>
          <SecretDisplay secret={setup!.secret} />
          <p className="mt-2">3. Ingrese el código de 6 dígitos que muestra la aplicación para confirmar.</p>
        </div>
      </div>

      <form className="flex items-end gap-2" onSubmit={handleSubmit(onConfirm)} noValidate>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700" htmlFor="confirm-code">
            Código de verificación
          </label>
          <input
            id="confirm-code"
            type="text"
            autoComplete="one-time-code"
            placeholder="123456"
            className="input-field mt-1 tracking-widest"
            {...register("code", { required: true })}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={confirmTwoFactor.isPending}>
          {confirmTwoFactor.isPending ? "Confirmando..." : "Confirmar y activar"}
        </button>
      </form>
    </div>
  );
}

function SecretDisplay({ secret }: { secret: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-1 flex items-center gap-2">
      <code className="rounded bg-white px-2 py-1 font-mono text-xs tracking-widest text-gray-800 border border-gray-200">{secret}</code>
      <button type="button" className="text-gray-400 hover:text-gray-600" onClick={copy} title="Copiar">
        {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function RecoveryCodesPanel({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  const copyAll = async () => {
    await navigator.clipboard.writeText(codes.join("\n"));
    toast.success("Códigos copiados al portapapeles.");
  };

  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2 text-amber-800">
        <KeyRound size={16} />
        <h3 className="text-sm font-semibold">Guarde sus códigos de recuperación</h3>
      </div>
      <p className="text-sm text-amber-800">
        Autenticación en dos pasos activada. Estos 10 códigos de un solo uso le permiten iniciar sesión si pierde acceso a su
        aplicación autenticadora. <strong>No volverán a mostrarse.</strong>
      </p>
      <div className="grid grid-cols-2 gap-2 rounded bg-white p-3 font-mono text-sm">
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary" onClick={copyAll}>
          <Copy size={14} className="mr-1.5" /> Copiar todos
        </button>
        <label className="flex items-center gap-2 text-sm text-amber-800">
          <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
          Ya guardé estos códigos en un lugar seguro
        </label>
      </div>
      <button type="button" className="btn-primary" disabled={!acknowledged} onClick={onDone}>
        {acknowledged ? <ShieldCheck size={14} className="mr-1.5" /> : <Loader2 size={14} className="mr-1.5 opacity-0" />}
        Listo
      </button>
    </div>
  );
}
